import type { MouseEvent } from "react";
import { SanitizeHTMLString } from "@circle-react-shared/uikit/SanitizeContent";
import { Button } from "@circle-react-uikit/Button";
import { Typography } from "@circle-react-uikit/Typography";

export interface EmptyStateProps {
  description?: string;
  fontSize?: "sm" | "md";
  header: string;
  onPrimaryClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  onSecondaryClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  onTertiaryClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  primaryDisabled?: boolean;
  primaryLabel?: string;
  secondaryDisabled?: boolean;
  secondaryLabel?: string;
  tertiaryDisabled?: boolean;
  tertiaryLabel?: string;
}

export const EmptyState = ({
  header,
  description,
  primaryLabel,
  primaryDisabled = false,
  onPrimaryClick,
  secondaryLabel,
  secondaryDisabled = false,
  onSecondaryClick,
  tertiaryLabel,
  tertiaryDisabled = false,
  onTertiaryClick,
  fontSize = "md",
}: EmptyStateProps) => {
  const hasPrimary = Boolean(onPrimaryClick && primaryLabel);
  const hasSecondary = Boolean(onSecondaryClick && secondaryLabel);
  const hasTertiary = Boolean(onTertiaryClick && tertiaryLabel);
  let Title: React.ElementType;
  let titleProps: { weight?: "semibold" } = {};
  let Description: React.ElementType;

  const isFontSizeSm = fontSize === "sm";
  if (isFontSizeSm) {
    Title = Typography.TitleSm;
    titleProps = { weight: "semibold" };
    Description = Typography.BodySm;
  } else {
    Title = Typography.TitleMd;
    Description = Typography.BodyMd;
  }

  return (
    <div
      className="flex size-full flex-col items-center justify-center gap-9 p-8 text-center"
      data-testid="empty-state"
    >
      <div className="flex max-w-lg flex-col gap-3">
        <Title {...titleProps}>{header}</Title>
        {description && (
          <Description>
            <SanitizeHTMLString content={description} />
          </Description>
        )}
      </div>
      <div className="flex gap-3">
        {hasPrimary && (
          <Button
            type="button"
            variant="circle"
            onClick={onPrimaryClick}
            disabled={primaryDisabled}
          >
            {primaryLabel}
          </Button>
        )}
        {hasSecondary && (
          <Button
            type="button"
            variant="secondary"
            onClick={onSecondaryClick}
            disabled={secondaryDisabled}
          >
            {secondaryLabel}
          </Button>
        )}
      </div>
      {hasTertiary && (
        <Button
          type="button"
          variant="link"
          onClick={onTertiaryClick}
          disabled={tertiaryDisabled}
        >
          {tertiaryLabel}
        </Button>
      )}
    </div>
  );
};
